<template>
  <div class="impact-stories-page">
    <div class="impact-banner">
      <div class="banner-content">
        <h1>{{ title }}</h1>
        <h2>{{ subtitle }}</h2>
        <p>{{ description }}</p>
      </div>
      <div class="impact-stickers">
        <div v-for="(item, index) in impactData" :key="index" class="sticker" :class="{ 'middle': index === 1, 'last': index === impactData.length - 1 }">
          <span class="sticker-value">{{ item.value }}</span>
          <div class="sticker-label">{{ item.label }}</div>
        </div>
      </div>
    </div>
    <section class="section--stories">
      <v-container>
        <v-row justify="center" class="section__row">
          <v-col
            class="section__col"
            :cols="12"
            :md="5"
            :sm="8"
            :lg="4"
            :xs="12"
            v-for="story in stories"
            :key="story.id"
          >
            <StoryCard 
              :title="story.name"
              :description="story.description"
              :image="story.image"
              :read-more-text="'View Story'"
              :route="`/impact-story/${story.slug}`"
              :aspect-ratio="1"
            />
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
  <GetInTouch :backgroundColor="'#DEF3FF'" />
</template>

<script setup>
import { ref } from 'vue';
import StoryCard from '../customers/Sections/StoryCard.vue';
import GetInTouch from './../service/components/GetInTouch.vue';
import { storiesData } from './StoriesData.js';

const title = 'Impact Stories';
const subtitle = 'Real Stories. Real Impact';
const description = 'Explore how DIGU is empowering Nepali professionals and communities through education, innovation, and opportunity—one story at a time.';
const impactData = ref([
  { value: '06', label: 'Total Jobs Created' },
  { value: '01', label: 'Scholarships Awarded' },
  { value: '10', label: 'Nepali Families Supported' },
]);

// Variables from the Excel screenshot
const onboardedByClients = 6;
const onboardedForDigu = 11;
const internsHired = 0;
const scholarshipsAwarded = 2;
const livelihoodDrivenBusinessSupported = 2;

// Calculate Total Jobs Created
const totalJobsCreated = onboardedByClients + onboardedForDigu + internsHired;

// Calculate Families Supported
const familiesSupported = totalJobsCreated + scholarshipsAwarded + livelihoodDrivenBusinessSupported;

impactData.value[0].value = totalJobsCreated.toString();
impactData.value[1].value = scholarshipsAwarded.toString();
impactData.value[2].value = familiesSupported.toString();

const stories = ref(storiesData);

document.getElementById('loaderSection').classList.add('d-none');
</script>

<style scoped>
.impact-stories-page {
  background-color: white;
}

.impact-banner {
  background-color: #f5f5f5;
  text-align: center;
  padding: 50px 20px 60px 20px;
  position: relative;
  overflow: visible;
}

.banner-content {
  max-width: 1100px;
  margin: 0 auto;
}

h1 {
  font-size: 2.5em;
  color: #333;
  margin-bottom: 10px;
}

h2 {
  font-size: 1.5em;
  color: #666;
  margin-bottom: 20px;
}

p {
  font-size: 1em;
  color: #666;
  max-width: 600px;
  margin: 0 auto 5rem;
  line-height: 1.6;
}

.impact-stickers {
  width: 100%;
  max-width: 1000px;
  display: flex;
  justify-content: space-around;
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.sticker {
  background-color: #fff;
  border-radius: 12px;
  padding: 25px 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  text-align: center;
  width: 220px;
  position: relative;
  border: 1px solid #e0e0e0;
}

.sticker-value {
  display: block;
  font-size: 2.5em;
  color: #ff4d4d;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 8px;
}

.sticker-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  line-height: 1.3;
}

.middle {
  z-index: 12;
  transform: scale(1.05);
}

.section--stories {
  padding: 145px 0 60px 0;
  background-color: white;
}

.section__row {
  max-width: 1300px;
  margin-inline: auto;
}

.section__col {
  padding: 1rem 2rem;
  display: flex;
  justify-content: center;
}

@media only screen and (max-width: 770px) {
  .impact-banner {
    padding: 40px 15px 12rem 15px;
  }
  
  h1 {
    font-size: 2em;
  }
  
  h2 {
    font-size: 1.3em;
  }
  
  p {
    font-size: 0.9em;
    margin-bottom: 30px;
  }
  
  .impact-stickers {
    flex-direction: column;
    gap: 15px;
    bottom: -180px;
    align-items: center;
  }
  
  .sticker {
    width: 260px;
    padding: 20px;
  }
  
  .middle {
    transform: none;
  }
  
  .section--stories {
    padding: 16rem 0 40px 0;
    max-width: 100%;
  }
}

@media only screen and (max-width: 690px) {
  .section__col {
    flex: 0 0 100%;
  }
  
  .impact-stickers {
    bottom: -170px;
  }
  
  .section--stories {
    padding: 15rem 0 40px 0;
  }
  
  .section__col {
    padding: 1rem;
  }
}

@media only screen and (max-width: 480px) {
  .impact-banner {
    padding: 30px 30px 11rem 30px;
  }
  
  h1 {
    font-size: 1.8em;
  }
  
  .sticker {
    width: 210px;
    padding: 18px 15px;
  }
  
  .sticker-value {
    font-size: 2.2em;
  }
  
  .sticker-label {
    font-size: 13px;
  }
  
  .impact-stickers {
    bottom: -160px;
  }
  
  .section--stories {
    padding: 14rem 0 40px 0;
    max-width: 100%;
  }
}
</style>